"use strict";
/* globals module */
/**
 * @module calendarRsvpClient
 * @description  CalendarRsvpClient provides an easy way to consume CalendarRsvp REST API end-points. In order to obtain needed routes `calendarRsvpClient` uses `calendarRsvpRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../../../httpApi");
var __1 = require("../../");
var CalendarRsvpClient = /** @class */ (function () {
    function CalendarRsvpClient(calendarRsvpBatchClient, calendarRsvpRoute, apiClient) {
        this.calendarRsvpBatchClient = calendarRsvpBatchClient;
        this.calendarRsvpRoute = calendarRsvpRoute;
        this.apiClient = apiClient;
    }
    Object.defineProperty(CalendarRsvpClient.prototype, "routeDefinition", {
        get: function () {
            return this.calendarRsvpRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarRsvpClient.prototype, "batch", {
        get: function () {
            return this.calendarRsvpBatchClient;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of CalendarEventRsvp resources matching the given criteria.
     * @method
     * @param calendarId Calendar id which uniquely identifies Calendar resource.
     * @param options Query resource GetCalendarOptions object.
     * @returns A promise that is resolved once the find action has been performed.
     * @example calendarRsvpClient.find(calendarId, {
                    pageNumber: 1,
                    pageSize: 10,
                    orderBy: '<field>',
                    orderDirection: '<asc|desc>',
                    search: '<search-phrase>',
                    ids: '<ids>',
                    InvitationTypeIds: '<invitation-type-ids>',
                    InvitationOnly: <true|false>,
                    statusIds: '<status-ids>',
                    from: '<from-date>',
                    to: '<to-date>',
                    registrationCloseFrom: '<registration-start-date>',
                    registrationCloseTo: '<registration-end-date>'
                })
                .then(function (collection) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarRsvpClient.prototype.find = function (calendarId, options) {
        return this.apiClient.get(this.routeDefinition.find(calendarId, options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns the CalendarEventRsvp resource.
     * @method
     * @param calendarId Calendar id which uniquely identifies Calendar resource.
     * @param id CalendarEventRsvp id which uniquely identifies CalendarEventRsvp resource that needs to be retrieved.
     * @param options Query resource options object.
     * @returns A promise that is resolved once the get action has been performed.
     * @example calendarRsvpClient.get()
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarRsvpClient.prototype.get = function (calendarId, id, options) {
        return this.apiClient.get(this.routeDefinition.get(calendarId, id, options));
    };
    /**
     * Returns a promise that is resolved once the link CalendarEventRsvp action has been performed; this action links the CalendarEventRsvp to the calendarEvent resource.
     * @method
     * @param calendarId Calendar id which uniquely identifies Calendar resource.
     * @param data A CalendarEventRsvp object that needs to be inserted into the system.
     * @returns A promise that is resolved once the link CalendarEventRsvp action has been performed.
     * @example calendarRsvpClient.link(calendarId, {
                    InvitationOnly: <true|false>,
                    InvitationType: <calendar-rsvp-invitation-type>,
                    InvitationTypeId: '<invitation-type-id>',
                    Json: '<json>',
                    MaxSlots: <max-slots>,
                    MinSlots: <min-slots>,
                    RegistrationCloseDate: '<registration-close-date>',
                    TotalSlots: <total-slots>
                })
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarRsvpClient.prototype.link = function (calendarId, data) {
        return this.apiClient.post(this.routeDefinition.link(calendarId, data), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the update CalendarEventRsvp action has been performed; this action updates a CalendarEventRsvp resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `calendarRsvpRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(calendarEventRsvp);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param calendarId Calendar id which uniquely identifies Calendar resource.
     * @param data A CalendarEventrSVP object used to update specified CalendarEventRsvp resource.
     * @returns A promise that is resolved once the update CalendarEventRsvp action has been performed.
     * @example calendarEventRsvp is a resource previously fetched using get action.
                calendarEventRsvp.name = '<name>';
                calendarRsvpClient.update(calendarEventRsvp)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarRsvpClient.prototype.update = function (calendarId, data) {
        return this.apiClient.put(this.routeDefinition.update(calendarId, data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the unlink action has been performed. This action will unlink a CalendarEventRsvp from the CalendarEvent if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `calendarRsvpRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(calendarEventRsvp);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param calendarId Calendar id which uniquely identifies Calendar resource.
     * @param data An calendarEventRsvp object used to delete specified CalendarEventRsvp resource.
     * @returns A promise that is resolved once the unlink action has been performed.
     * @example calendarEventRsvp is a resource previously fetched using get action.
                calendarRsvpClient.unlink(calendarId, calendarEventRsvp)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarRsvpClient.prototype.unlink = function (calendarId, data) {
        return this.apiClient.delete(this.routeDefinition.unlink(calendarId, data));
    };
    /**
     * Returns a promise that is resolved once the purge action has been performed. This action will remove all CalendarEventRsvp resources from the system if succesfully completed.
     * @method
     * @param calendarId Calendar id which uniquely identifies Calendar resource.
     * @param data A CalendarEvent object that will have it's rsvp's purged
     * @returns A promise that is resolved once the purge action has been performed.
     * @example calendarRsvpClient.purge()
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarRsvpClient.prototype.purge = function (calendarId, data) {
        return this.apiClient.delete(this.routeDefinition.purge(calendarId, data));
    };
    var _a, _b, _c;
    CalendarRsvpClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(__1.TYPES.CalendarRsvpBatchClient)),
        tslib_1.__param(1, inversify_1.inject(__1.TYPES.CalendarRsvpRoute)),
        tslib_1.__param(2, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof __1.CalendarRsvpBatchClient !== "undefined" && __1.CalendarRsvpBatchClient) === "function" ? _a : Object, typeof (_b = typeof __1.CalendarRsvpRoute !== "undefined" && __1.CalendarRsvpRoute) === "function" ? _b : Object, typeof (_c = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _c : Object])
    ], CalendarRsvpClient);
    return CalendarRsvpClient;
}());
exports.CalendarRsvpClient = CalendarRsvpClient;
/**
 * @copyright (c) 2017 Mono Ltd
 * @license MIT
 * @author Mono Ltd
 * @overview
 ***Notes:**
 - Refer to the [Baasic REST API](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route definition.
 */ 
